import { validationConstants } from "constants/formValidation";
import { adjustmentTypesMapping, orderTypeConstants } from "constants/order";
import { ToastContext } from "context/ToastContext";
import dayjs from "dayjs";
import { parseDate, parseTime } from "helpers/date";
import { removeClearPatientFee } from "helpers/fee";
import {
  parsePrice,
  centsToDollars,
  calculateDiscountPercent,
} from "helpers/price";
import { roundToX } from "helpers/round";
import useFormValidation from "hooks/useFormValidation";
import { useOrder } from "hooks/useOrder";
import usePrice from "hooks/usePrice";
import { sizer } from "layout/styles/styled/sizer";
import { Label } from "layout/typography/Label";
import { isEmpty, sumBy } from "lodash";
import { addQuantityToRow } from "pages/CreateOrder/components/ServiceDetails/helpers";
import {
  validateScheduledFirstCommunicationDate,
  parseDateOfService,
  validateInputDatePicker,
  validateInputTime,
} from "pages/CreateOrder/utils/helpers";
import {
  handleChangeDateWithMask,
  handleChangeTimeWithMask,
} from "pages/CreateOrder/utils/mask";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useMemo, useContext, useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useUpdateOrderMutation } from "store/queries/order";
import {
  toggleDisplayAdjustments,
  toggleDisplayPatientResponsibility,
  toggleDisplayProviderPayoutCalculations,
} from "store/slices/serviceDetails";
import styled from "styled-components";
import {
  IDefaultServiceDetails,
  IOrder,
  IPatientResponsibilityServiceDetails,
} from "types/Order/Order";
import { IProviderAmount } from "types/Payment";
import { SpecialtyList, SpecialtyListDict } from "types/Provider/Provider";

import PatientBenefits from "../PatientBenefits/PatientBenefits";
import PayerDetails from "../PayerDetails/PayerDetails";
import { normalizePaymentPlanBreakdown } from "../ProviderPayoutCalculations/helpers";
import ProviderPayoutCalculations from "../ProviderPayoutCalculations/ProviderPayoutCalculations";

type ProviderRow = {
  code: string;
  cptDescription: string;
  amountInCents: number;
  specialty?: string;
  providerName?: string;
  quantity?: number;
};

const StyledCard = styled(Card)`
  margin-bottom: ${sizer(2)};
  border: 1px solid var(--gray-new-200) !important;
`;

const CardHeader = styled.div`
  border-bottom: 1 solid gray;
`;

const FieldsContainer = styled.div`
  display: flex;
  justify-content: end;
  margin-top: ${sizer(4)};
`;

const StyledDataTable = styled(DataTable)`
  margin-top: ${sizer(6)};

  tr {
    background: var(--gray-50);
  }

  span.p-column-title {
    font-weight: var(--font-weight-semibold);
    font-size: var(--fontsize-contents);
    color: var(--color-black-4);
  }

  td {
    font-weight: var(--font-weight-medium);
    font-size: var(--fontsize-contents);
    color: var(--color-matte-black);
  }
`;

const Discount = styled.div`
  text-align: end;
  color: var(--color-purple);
  font-weight: var(--font-weight-semibold);
  font-size: var(--fontsize-contents);
  margin-right: var(--space-sm);
  padding-top: 0;
  padding-bottom: 0;

  @media (min-width: 720px) {
    text-align: center;
  }

  @media (min-width: 1020px) {
    text-align: end;
  }
`;

const orderTypeToLocationKey: Partial<Record<string, string>> = {
  [orderTypeConstants.BUNDLED]: "bundled",
  [orderTypeConstants.PATIENT_RESPONSIBILITY]: "patientResponsibility",
  [orderTypeConstants.ED]: "ed",
  [orderTypeConstants.BARIATRICS]: "bariatrics",
  [orderTypeConstants.GFE]: "gfe",
};

function isLocationEnabledForOrderType(
  orderType: string,
  locationEnabled?: Record<string, boolean>
): boolean {
  const key = orderTypeToLocationKey[orderType];
  return !!(key && locationEnabled?.[key]);
}

interface IServiceDetailsEdit {
  dateOfService: string;
  accountNumber: string;
  timeOfService: string;
  deductible: number;
  outOfPocketMax: number;
  flatCopay: number;
  coinsurancePercent: number;
  amountLeft: number;
  scheduledFirstCommunicationDate?: Date | null | string;
  scheduledFirstCommunicationSent: boolean;
  scheduledCommunicationCustomText: string | null;
  accountLocationId?: string | null;
}

function ServiceDetails({
  order,
  onUpdateOrder,
}: {
  order: IOrder;
  onUpdateOrder: () => void;
}) {
  const {
    control,
    trigger,
    getValues,
    setValue,
    formState: { errors },
    watch,
  } = useFormContext<IServiceDetailsEdit>();
  const [editing, setEdit] = useState(false);
  const [editingAmount, setEditAmount] = useState(false);
  const dispatch = useDispatch();
  const {
    displayAdjustments,
    displayPatientResponsibility,
    displayProviderPayoutCalculations,
  } = useSelector((state: any) => state.serviceDetailsSlice);
  const { current: toastElement } = useContext(ToastContext);
  const { handleOnBlurField, getFormErrorMessage } = useFormValidation();
  const scheduledFirstCommunicationDate = watch(
    "scheduledFirstCommunicationDate"
  );
  const scheduledFirstCommunicationSent = watch(
    "scheduledFirstCommunicationSent"
  );
  const dateOfService = watch("dateOfService");
  const timeOfService = watch("timeOfService");
  const accountNumber = watch("accountNumber");

  const { getClearPriceByOrderType, insuranceAmounts, getInsuranceAmounts } =
    usePrice();
  const { isDefaultOrderType } = useOrder(order);

  const [updateOrder] = useUpdateOrderMutation();
  const firstColumnTemplate = (row: ProviderRow) => {
    return <span className="font-bold">{row.providerName}</span>;
  };

  const firstColumnBold = (text: string) => {
    return <span className="font-bold">{text}</span>;
  };

  useEffect(() => {
    setValue("dateOfService", parseDate(order.dateOfService));
    setValue("accountNumber", order.accountNumber);
    setValue("timeOfService", parseTime(order.dateOfService));
    setValue(
      "deductible",
      centsToDollars(order?.gfeQuote?.remainingDeductibleInCents || 0)
    );
    setValue(
      "outOfPocketMax",
      centsToDollars(order?.gfeQuote?.outOfPocketMaxInCents || 0)
    );
    setValue(
      "flatCopay",
      centsToDollars(order?.gfeQuote?.flatCopayInCents || 0)
    );
    setValue("coinsurancePercent", order?.gfeQuote?.coinsurancePercent || 0);
    setValue(
      "scheduledFirstCommunicationDate",
      order?.scheduledFirstCommunicationDate
        ? dayjs.utc(order.scheduledFirstCommunicationDate).toDate()
        : null
    );
    setValue(
      "scheduledFirstCommunicationSent",
      order?.scheduledFirstCommunicationSent || false
    );
    setValue(
      "accountLocationId",
      order?.accountLocation?.id ?? order?.accountLocationId ?? null
    );
  }, [order, setValue]);

  const columns = useMemo(() => {
    if (order.orderType === orderTypeConstants.PATIENT_RESPONSIBILITY) {
      return [
        { field: "code", header: "CPT Code" },
        { field: "cptDescription", header: "CPT Description" },
      ];
    }

    return [
      {
        field: "code",
        header: "CPT Code/Name",
        body: (row: ProviderRow) =>
          firstColumnBold(`${row.code} - ${row.cptDescription}`),
        headerStyle: { width: "30%" },
      },
      {
        field: "specialty",
        header: "Specialty",
        body: (row: ProviderRow) =>
          SpecialtyListDict[row.specialty as SpecialtyList],
        headerStyle: { width: "15%" },
      },
      {
        header: "Amount",
        field: "amountInCents",
        body: (row: ProviderRow) => {
          const quantity = row.quantity || 1;
          return parsePrice(centsToDollars(row.amountInCents * quantity));
        },
        headerStyle: { width: "15%" },
      },
      {
        field: "providerName",
        header: "Provider",
        headerStyle: { width: "25%" },
      },
      { field: "quantity", header: "Quantity", headerStyle: { width: "15%" } },
    ];
  }, [order]);

  const columnsAdjustmentAndPatientResponsibility = useMemo(() => {
    return [
      {
        field: "providerName",
        header: "Provider",
        body: (row: ProviderRow) => firstColumnTemplate(row),
      },
      {
        field: "code",
        header: "CPT Code/Name",
      },
      {
        field: "specialty",
        header: "Description",
      },
      {
        field: "amountInCents",
        header: "Amount",
        body: (row: ProviderRow) =>
          parsePrice(centsToDollars(row.amountInCents)),
      },
    ];
  }, [order]);

  const parseDataToPatientResponsibility = useMemo(() => {
    return (
      order.gfeQuote?.pricingBreakdown.procedureBreakdown?.map(
        (aPatientResponsibility) => {
          const { code, owedByPatient, providerName } = aPatientResponsibility;
          const { coinsurance, deductible, total, selfPay } = owedByPatient;

          let description = "";

          if (coinsurance > 0 && deductible > 0) {
            description = "Deductible + Coinsurance";
          } else if (coinsurance > 0) {
            description = "Coinsurance";
          } else if (deductible > 0) {
            description = "Deductible";
          } else if (selfPay > 0) {
            description = "Self-Pay";
          }
          return {
            code,
            amountInCents: total,
            specialty: description,
            providerName,
          };
        }
      ) || []
    );
  }, [order.gfeQuote]);

  const parseDataToAdjustment = useMemo(() => {
    return (
      order.gfeQuote?.pricingBreakdown.adjustments?.map(
        (aPatientResponsibility) => {
          const { procedure, discountType, discountedPrice } =
            aPatientResponsibility;
          const { code, providerName } = procedure;

          return {
            code,
            amountInCents: discountedPrice,
            specialty: adjustmentTypesMapping[discountType],
            providerName,
          };
        }
      ) || []
    ).sort((a, b) => {
      const aKey = `${a.providerName}-${a.code}`;
      const bKey = `${b.providerName}-${b.code}`;
      return aKey.localeCompare(bKey);
    });
  }, [order.gfeQuote]);

  const paymentBreakdownDetails = useMemo(() => {
    const list = order?.paymentFullBreakdown?.bundledProviderAmounts;

    const sortedList = list?.length
      ? [...list].sort((a, b) => b.providerPriority - a.providerPriority)
      : [];

    // Transform IProviderAmount to ProviderAmountRow
    return sortedList.map((item: IProviderAmount) => ({
      ...item,
      patientPortion: item.patientPortion || 0, // Provide a default value or calculate
      remainingToPayProvider: item.remainingToPayProvider || 0, // Provide a default value or calculate
    }));
  }, [order]);

  const showScheduledDate = useMemo(() => {
    if (scheduledFirstCommunicationDate && scheduledFirstCommunicationSent) {
      return false;
    }
    return true;
  }, [scheduledFirstCommunicationDate, scheduledFirstCommunicationSent]);

  const showLocationField = useMemo(() => {
    if (order.accountLocation) {
      return true;
    }
    const { account } = order;
    return (
      isLocationEnabledForOrderType(
        order.orderType,
        account?.locationEnabled
      ) && (account?.accountLocations?.length ?? 0) > 0
    );
  }, [
    order.accountLocation,
    order.orderType,
    order.account?.locationEnabled,
    order.account?.accountLocations,
  ]);

  const clearFee = useMemo(() => {
    return order?.paymentFullBreakdown?.clearFeeAmountInCents;
  }, [order]);

  const payments = order.paymentPlanBreakdown
    ? normalizePaymentPlanBreakdown(order.paymentPlanBreakdown.payments)
    : undefined;

  // Extract splitAmountsByProvider from both breakdowns
  const paymentPlanSplitAmountsByProvider = useMemo(() => {
    return (order.paymentPlanBreakdown as any)?.splitAmountsByProvider;
  }, [order.paymentPlanBreakdown]);

  const fullBreakdownSplitAmountsByProvider = useMemo(() => {
    return (order.paymentFullBreakdown as any)?.splitAmountsByProvider;
  }, [order.paymentFullBreakdown]);

  useEffect(() => {
    if (!isEmpty(order.gfeQuote)) {
      getInsuranceAmounts(order.gfeQuote.pricingBreakdown);
    }
  }, [order.gfeQuote]);

  useEffect(() => {
    validateScheduledFirstCommunicationDate(
      scheduledFirstCommunicationDate,
      dateOfService,
      scheduledFirstCommunicationSent
    );
    trigger("scheduledFirstCommunicationDate");
  }, [
    scheduledFirstCommunicationDate,
    dateOfService,
    scheduledFirstCommunicationSent,
  ]);
  const paymentInfo = useMemo(() => {
    const { total, clearPrice } = getClearPriceByOrderType({ order });

    const subtotalValue = sumBy(
      order.serviceDetails.procedures as any,
      "amountInCents"
    );

    const subTotal = isDefaultOrderType ? subtotalValue : total;
    const totalWithDiscount = clearPrice;

    // We calculate Clear Patient Fee using the percentage saved in the order table
    let clearPatientFee = 0;
    let clearPatientFeeInDollars = 0;

    // For GFE orders, patientResponsibilityEstimateAmountInCents now includes the Clear Patient Fee, when it exists
    // So we need to calculate the subtotal by subtracting the Clear Patient Fee
    if (
      order.orderType === orderTypeConstants.GFE &&
      order.clearPatientFeeValueInPercentage &&
      order.clearPatientFeeValueInPercentage > 0
    ) {
      // Convert to dollars, remove fee with rounding logic, convert back
      const totalInDollars = centsToDollars(total);
      const subtotalInDollars = removeClearPatientFee(
        totalInDollars,
        order.clearPatientFeeValueInPercentage
      );
      const subtotalAmount = subtotalInDollars * 100; // back to cents for internal calc
      clearPatientFee = total - subtotalAmount;
      clearPatientFeeInDollars = centsToDollars(clearPatientFee);
    }

    // For GFE orders, calculate the display values
    let rawTotal;
    let rawTotalWithPatientFee;

    if (order.orderType === orderTypeConstants.GFE && clearPatientFee > 0) {
      // total field contains the total including Clear Patient Fee
      rawTotalWithPatientFee = parsePrice(centsToDollars(total));
      // Calculate subtotal by subtracting Clear Patient Fee
      rawTotal = parsePrice(centsToDollars(total - clearPatientFee));
    } else {
      // For non-GFE orders or GFE orders without Clear Patient Fee
      rawTotal = parsePrice(centsToDollars(subTotal));
      // rawTotalWithPatientFee not needed since we don't display it in this case
    }

    const rawTotalWithDiscount = parsePrice(centsToDollars(totalWithDiscount));

    let providerFinalAmount;
    if (order.orderType === orderTypeConstants.PATIENT_RESPONSIBILITY) {
      const providerAmountInCents =
        order?.paymentFullBreakdown?.patientRespFacilityAmountInCents;
      providerFinalAmount =
        providerAmountInCents &&
        parsePrice(centsToDollars(providerAmountInCents));
    }

    const discountPercentForPatientResponsibility = roundToX(
      calculateDiscountPercent(total, totalWithDiscount),
      2
    );

    return {
      rawTotalWithDiscount,
      rawTotal,
      rawTotalWithPatientFee,
      clearPatientFeeInDollars,
      discountPercentForPatientResponsibility,
      providerFinalAmount,
    };
  }, [order, order.serviceDetails, order.serviceDetails.procedures]);

  const saveButtonEnabled = useMemo(() => {
    const initialDateOfService = order.dateOfService;
    const isInitialDateInPast = dayjs(initialDateOfService).isBefore(
      dayjs(),
      "day"
    );

    const dateOfServiceIsValid = isInitialDateInPast
      ? !!dateOfService // If initial date is in past, just check if date exists
      : dateOfService &&
        validateInputDatePicker(dateOfService, "future") === true;

    const timeOfServiceIsValid = timeOfService
      ? validateInputTime(timeOfService) === true
      : true;
    const accountNumberIsValid = !!accountNumber;
    const scheduledFirstCommunicationDateIsValid =
      validateScheduledFirstCommunicationDate(
        scheduledFirstCommunicationDate,
        dateOfService,
        scheduledFirstCommunicationSent
      ) === true;

    return (
      dateOfServiceIsValid &&
      timeOfServiceIsValid &&
      accountNumberIsValid &&
      scheduledFirstCommunicationDateIsValid
    );
  }, [
    order.dateOfService,
    dateOfService,
    timeOfService,
    accountNumber,
    scheduledFirstCommunicationDate,
    scheduledFirstCommunicationSent,
  ]);

  const resetForm = () => {
    setValue("dateOfService", parseDate(order.dateOfService));
    setValue("accountNumber", order.accountNumber);
    setValue("timeOfService", parseTime(order.dateOfService));
    setValue(
      "scheduledFirstCommunicationDate",
      order?.scheduledFirstCommunicationDate
        ? dayjs.utc(order.scheduledFirstCommunicationDate).toDate()
        : null
    );
    setValue(
      "scheduledFirstCommunicationSent",
      order?.scheduledFirstCommunicationSent || false
    );
    setValue(
      "accountLocationId",
      order?.accountLocation?.id ?? order?.accountLocationId ?? null
    );
  };

  const mapServiceDetailsUpdateRequest = (values: IServiceDetailsEdit) => ({
    dateOfService: parseDateOfService({
      dateOfService: values.dateOfService,
      timeOfService: values.timeOfService,
    }),
    accountNumber: values.accountNumber,
    scheduledFirstCommunicationDate: values.scheduledFirstCommunicationDate
      ? dayjs(values.scheduledFirstCommunicationDate)
          .utc()
          .format("YYYY-MM-DD HH:mm")
      : null,
    accountLocationId: values.accountLocationId || null,
  });

  const submit = async () => {
    const isValid = await trigger("scheduledFirstCommunicationDate");
    if (!isValid) {
      return;
    }
    const values = mapServiceDetailsUpdateRequest(getValues());
    console.log("values", values);
    // return;
    updateOrder({ id: order.id, ...values })
      .unwrap()
      .then(() => {
        onUpdateOrder();
        toastElement?.show({
          summary: "Success!",
          severity: "success",
          detail: "Your order has been updated.",
        });
        setEdit(false);
      })
      .catch(() => {
        toastElement?.show({
          severity: "error",
          detail: "Try again later.",
          summary: "Something went wrong.",
        });
      });
  };

  const actionFooter = () => {
    return (
      <>
        <Button
          label="Save"
          icon="pi pi-check"
          onClick={submit}
          disabled={!saveButtonEnabled}
        />
        <Button
          label="Cancel"
          onClick={() => {
            resetForm();
            setEdit(false);
            setEditAmount(false);
          }}
          icon="pi pi-times"
          style={{ marginLeft: "0.5em" }}
          className="p-button-outlined-light"
        />
      </>
    );
  };

  const actionHeader = () => {
    return (
      <div className="flex flex-column col-12 align-items-end">
        <div>
          <Button
            icon="pi pi-pencil"
            onClick={() => setEdit(true)}
            className="p-button-outlined-light"
          />
        </div>
      </div>
    );
  };

  const orderTypeRules = useMemo(() => {
    const isGFEorPatientResponsibility =
      order.orderType === orderTypeConstants.PATIENT_RESPONSIBILITY ||
      order.orderType === orderTypeConstants.GFE;
    const isPatientResponsibility =
      order.orderType === orderTypeConstants.PATIENT_RESPONSIBILITY;
    const isGFE = order.orderType === orderTypeConstants.GFE;

    return { isGFEorPatientResponsibility, isPatientResponsibility, isGFE };
  }, [order.orderType]);

  const subtotalLabel = useMemo(() => {
    if (orderTypeRules.isGFEorPatientResponsibility) {
      if (orderTypeRules.isGFE && paymentInfo.clearPatientFeeInDollars > 0) {
        return "Subtotal estimated patient cost";
      }
      return "Total estimated patient cost";
    }
    return "Subtotal Cost";
  }, [
    orderTypeRules.isGFEorPatientResponsibility,
    orderTypeRules.isGFE,
    paymentInfo.clearPatientFeeInDollars,
  ]);

  return (
    <>
      {orderTypeRules.isGFE && (
        <>
          <PayerDetails order={order} />
          <PatientBenefits order={order} />
        </>
      )}
      <StyledCard title="Service Details" header={!editing && actionHeader()}>
        <div className="formgrid grid w-100">
          <div className="field flex flex-column col-12 md:col-4">
            <Label htmlFor="dateOfService">Date of service</Label>

            <Controller
              data-testid="dateOfService"
              name="dateOfService"
              control={control}
              rules={{
                validate: (date) => {
                  const initialDateOfService = order.dateOfService;
                  const isInitialDateInPast = dayjs(
                    initialDateOfService
                  ).isBefore(dayjs(), "day");

                  if (isInitialDateInPast) {
                    // If initial date is in past, just validate it's a valid date
                    return validateInputDatePicker(date, "any");
                  }
                  // Otherwise, validate it's a future date
                  return validateInputDatePicker(date, "future");
                },
                required: validationConstants.VISIT_INFO_FORM.DATE_OF_SERVICE,
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <>
                  <InputText
                    disabled={!editing}
                    id="dateOfService"
                    value={value || ""}
                    placeholder="99/99/9999"
                    onBlur={() =>
                      handleOnBlurField({
                        onBlur,
                        field: "dateOfService",
                        trigger,
                      })
                    }
                    onChange={(e) => handleChangeDateWithMask(e, onChange)}
                  />

                  {getFormErrorMessage("dateOfService", errors)}
                </>
              )}
            />
          </div>

          <div className="field flex flex-column col-12 md:col-4">
            <Label htmlFor="timeOfService">Time of service</Label>

            <Controller
              control={control}
              name="timeOfService"
              data-testid="timeOfService"
              rules={{
                validate: (time) => validateInputTime(time),
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <>
                  <InputText
                    disabled={!editing}
                    id="timeOfService"
                    value={value}
                    placeholder="HH:MM PM"
                    onBlur={() =>
                      handleOnBlurField({
                        onBlur,
                        field: "timeOfService",
                        trigger,
                      })
                    }
                    onChange={(e) => handleChangeTimeWithMask(e, onChange)}
                  />

                  {getFormErrorMessage("timeOfService", errors)}
                </>
              )}
            />
          </div>

          <div className="field flex flex-column col-12 md:col-4">
            <Label htmlFor="accountId">Account/ Visit ID</Label>

            <Controller
              name="accountNumber"
              control={control}
              rules={{
                required: validationConstants.VISIT_INFO_FORM.VISIT_ID,
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <>
                  <InputText
                    disabled={!editing}
                    data-testid="accountNumber"
                    id="accountNumber"
                    placeholder="Account ID"
                    autoComplete="off"
                    value={value}
                    onBlur={() =>
                      handleOnBlurField({
                        onBlur,
                        field: "accountNumber",
                        trigger,
                      })
                    }
                    onChange={(e) => onChange(e.target.value)}
                  />

                  {getFormErrorMessage("accountNumber", errors)}
                </>
              )}
            />
          </div>

          {showScheduledDate && (
            <div className="field flex flex-column col-12 md:col-4">
              <Label htmlFor="dateOfService">
                Scheduled first communication date
              </Label>

              <Controller
                data-testid="scheduledFirstCommunicationDate"
                name="scheduledFirstCommunicationDate"
                control={control}
                rules={{
                  validate: (date) =>
                    validateScheduledFirstCommunicationDate(
                      date,
                      dateOfService,
                      scheduledFirstCommunicationSent
                    ),
                }}
                render={({ field: { value } }) => (
                  <>
                    <Calendar
                      showIcon
                      placeholder="Schedule first message"
                      value={value}
                      todayButtonClassName="hidden"
                      disabled={
                        !!scheduledFirstCommunicationSent ||
                        !scheduledFirstCommunicationDate ||
                        !editing
                      }
                      minDate={dayjs().add(2, "minute").toDate()}
                      maxDate={dayjs(dateOfService, "MM/DD/YYYY")
                        .endOf("day")
                        .toDate()}
                      onChange={(e) => {
                        // Prevent clearing the date - only allow changing to a new date
                        if (e.value) {
                          setValue(
                            "scheduledFirstCommunicationDate",
                            e.value as string,
                            {
                              shouldValidate: true,
                              shouldDirty: true,
                              shouldTouch: true,
                            }
                          );
                        }
                      }}
                      showTime
                      hourFormat="12"
                      panelStyle={{
                        display: "flex",
                        gap: "2rem",
                      }}
                    />

                    {getFormErrorMessage(
                      "scheduledFirstCommunicationDate",
                      errors
                    )}
                  </>
                )}
              />
            </div>
          )}

          {showLocationField && (
            <div className="field flex flex-column col-12 md:col-4">
              <Label htmlFor="accountLocationId">Location</Label>
              <Controller
                data-testid="accountLocationId"
                name="accountLocationId"
                control={control}
                render={({ field: { onChange, value } }) =>
                  editing ? (
                    <Dropdown
                      id="accountLocationId"
                      data-testid="accountLocationId"
                      value={value ?? ""}
                      onChange={(e) => onChange(e.value ?? null)}
                      options={
                        order.account?.accountLocations ?? [
                          order.accountLocation,
                        ]
                      }
                      optionLabel="name"
                      optionValue="id"
                      placeholder="Select location"
                      className="w-full"
                      showClear
                    />
                  ) : (
                    <InputText
                      disabled
                      id="accountLocationId"
                      value={order.accountLocation?.name ?? "—"}
                    />
                  )
                }
              />
            </div>
          )}

          {!orderTypeRules.isGFEorPatientResponsibility && (
            <div className="field flex flex-column col-12 md:col-4">
              <Label htmlFor="estimatedVisitCost">
                Facility Patient Estimate
              </Label>

              <InputText
                disabled
                id="estimatedVisitCost"
                aria-labelledby="estimatedVisitCost"
                value={parsePrice(
                  centsToDollars(
                    (order.serviceDetails as IDefaultServiceDetails)
                      .estimatedVisitCost
                  )
                )}
              />
            </div>
          )}
        </div>

        {editing && actionFooter()}

        <StyledDataTable
          rowGroupMode="rowspan"
          style={{ marginLeft: "-1rem", marginRight: "-1rem" }}
          groupRowsBy="code"
          value={
            orderTypeRules.isPatientResponsibility
              ? order.serviceDetails.procedures
              : addQuantityToRow(order.serviceDetails.procedures)
          }
        >
          {columns.map((col: any) => (
            <Column
              key={col.field}
              field={col.field}
              header={col.header}
              body={col?.body}
              headerStyle={col.headerStyle}
            />
          ))}
        </StyledDataTable>
        {orderTypeRules.isGFE && (
          <>
            <div>
              <CardHeader
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  flexDirection: "row",
                  alignItems: "center",
                  paddingTop: sizer(5),
                }}
                onClick={() => {
                  dispatch(toggleDisplayAdjustments());
                }}
              >
                <span
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                  }}
                >
                  Adjustments
                </span>
                {displayAdjustments ? (
                  <i
                    className="pi pi-angle-up
"
                    style={{ fontSize: "1.5rem" }}
                  />
                ) : (
                  <i
                    className="pi pi-angle-down
"
                    style={{ fontSize: "1.5rem" }}
                  />
                )}
              </CardHeader>
              {displayAdjustments && (
                <StyledDataTable
                  rowGroupMode="rowspan"
                  style={{ marginLeft: "-1rem", marginRight: "-1rem" }}
                  emptyMessage="No adjustments"
                  value={parseDataToAdjustment}
                >
                  {columnsAdjustmentAndPatientResponsibility.map((col) => (
                    <Column
                      key={col.field}
                      field={col.field}
                      body={col?.body}
                      header={col?.header}
                    />
                  ))}
                </StyledDataTable>
              )}
            </div>
            <div>
              <CardHeader
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  flexDirection: "row",
                  alignItems: "center",
                  paddingTop: sizer(5),
                }}
                onClick={() => {
                  dispatch(toggleDisplayPatientResponsibility());
                }}
              >
                <span
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                  }}
                >
                  Patient Responsibility
                </span>
                {displayPatientResponsibility ? (
                  <i
                    className="pi pi-angle-up
"
                    style={{ fontSize: "1.5rem" }}
                  />
                ) : (
                  <i
                    className="pi pi-angle-down
"
                    style={{ fontSize: "1.5rem" }}
                  />
                )}
              </CardHeader>
              {displayPatientResponsibility && (
                <StyledDataTable
                  rowGroupMode="rowspan"
                  style={{ marginLeft: "-1rem", marginRight: "-1rem" }}
                  emptyMessage="No Patient Responsibility"
                  value={parseDataToPatientResponsibility}
                >
                  {columnsAdjustmentAndPatientResponsibility.map((col) => (
                    <Column
                      key={col.field}
                      field={col.field}
                      body={col?.body}
                      header={col?.header}
                    />
                  ))}
                </StyledDataTable>
              )}
            </div>
          </>
        )}
        {orderTypeRules.isGFEorPatientResponsibility && (
          <div>
            <CardHeader
              style={{
                display: "flex",
                justifyContent: "space-between",
                flexDirection: "row",
                alignItems: "center",
                paddingTop: sizer(5),
              }}
              onClick={() => {
                dispatch(toggleDisplayProviderPayoutCalculations());
              }}
            >
              <span
                style={{
                  fontSize: "1.1rem",
                  fontWeight: 700,
                }}
              >
                Provider Payout Calculations
              </span>
              <i
                className={
                  displayProviderPayoutCalculations
                    ? "pi pi-angle-up"
                    : "pi pi-angle-down"
                }
                style={{ fontSize: "1.5rem" }}
              />
            </CardHeader>
            {displayProviderPayoutCalculations && (
              <ProviderPayoutCalculations
                providerAmounts={paymentBreakdownDetails}
                clearFee={clearFee}
                payments={payments}
                fullBreakdownSplitPaymentDetails={
                  order?.paymentFullBreakdown?.splitPaymentDetails
                }
                paymentPlanSplitPaymentDetails={
                  order?.paymentPlanBreakdown?.splitPaymentDetails
                }
                orderStatus={order.status}
                orderId={order.id}
                onUpdateOrder={onUpdateOrder}
                clearPatientFeeValueInPercentage={
                  order.clearPatientFeeValueInPercentage
                }
                paymentPlanSplitAmountsByProvider={
                  paymentPlanSplitAmountsByProvider
                }
                fullBreakdownSplitAmountsByProvider={
                  fullBreakdownSplitAmountsByProvider
                }
                orderType={order.orderType}
                paymentPlan={order?.paymentPlan?.find((p) => p.active)}
              />
            )}
          </div>
        )}
        <div>
          {orderTypeRules.isPatientResponsibility && (
            <FieldsContainer>
              <div className="flex align-items-center">
                <label
                  htmlFor="totalInsuranceAllowable"
                  className="mr-2 flex align-items-center"
                >
                  Total Insurance Allowable
                </label>

                <InputText
                  disabled
                  id="totalInsuranceAllowable"
                  aria-labelledby="totalInsuranceAllowable"
                  value={
                    (
                      order?.serviceDetails as IPatientResponsibilityServiceDetails
                    )?.totalInsuranceAllowableInCents == null
                      ? ""
                      : parsePrice(
                          centsToDollars(
                            (
                              order?.serviceDetails as IPatientResponsibilityServiceDetails
                            )?.totalInsuranceAllowableInCents || 0
                          )
                        )
                  }
                />
              </div>
            </FieldsContainer>
          )}
          {orderTypeRules.isGFE && (
            <>
              <FieldsContainer>
                <div className="flex align-items-center">
                  <label
                    htmlFor="totalInsuranceAllowable"
                    className="mr-2 flex align-items-center"
                  >
                    Total Insurance Allowable
                  </label>

                  <InputText
                    disabled
                    id="totalInsuranceAllowable"
                    aria-labelledby="totalInsuranceAllowable"
                    value={parsePrice(
                      centsToDollars(
                        insuranceAmounts?.totalAllowedInsuranceAmount
                      )
                    )}
                  />
                </div>
              </FieldsContainer>
              <FieldsContainer>
                <div className="flex align-items-center">
                  <label
                    htmlFor="totalInsurancePortion"
                    className="mr-2 flex align-items-center"
                  >
                    Total Insurance Portion
                  </label>

                  <InputText
                    disabled
                    id="totalInsurancePortion"
                    aria-labelledby="totalInsurancePortion"
                    value={parsePrice(
                      centsToDollars(insuranceAmounts?.totalInsurancePayInCents)
                    )}
                  />
                </div>
              </FieldsContainer>
            </>
          )}
          <FieldsContainer>
            <div className="flex align-items-center">
              <label
                htmlFor="subtotal"
                className="mr-2 flex align-items-center"
              >
                {subtotalLabel}
              </label>

              <InputText
                disabled
                id="subtotal"
                aria-labelledby="subtotal"
                value={paymentInfo.rawTotal}
              />
            </div>
          </FieldsContainer>

          {orderTypeRules.isGFE && paymentInfo.clearPatientFeeInDollars > 0 && (
            <FieldsContainer>
              <div className="flex align-items-center">
                <label className="mr-2 flex align-items-center">
                  Clear Patient Fee ({order.clearPatientFeeValueInPercentage}%)
                </label>
                <InputText
                  disabled
                  value={parsePrice(paymentInfo.clearPatientFeeInDollars)}
                />
              </div>
            </FieldsContainer>
          )}

          {orderTypeRules.isGFE && paymentInfo.clearPatientFeeInDollars > 0 && (
            <FieldsContainer>
              <div className="flex align-items-center">
                <label className="mr-2 flex align-items-center">
                  Total estimated patient cost
                </label>
                <InputText
                  disabled
                  value={paymentInfo.rawTotalWithPatientFee}
                />
              </div>
            </FieldsContainer>
          )}

          <FieldsContainer>
            <div className="flex align-items-center">
              <label
                htmlFor="totalPrice"
                className="mr-2 flex align-items-center"
              >
                {orderTypeRules.isGFEorPatientResponsibility
                  ? "Clear price offered to patient"
                  : "Clear Price for Patient"}
              </label>

              <InputText
                disabled
                id="totalPrice"
                aria-labelledby="totalPrice"
                value={paymentInfo.rawTotalWithDiscount}
              />
            </div>
          </FieldsContainer>

          {orderTypeRules.isGFEorPatientResponsibility && (
            <Discount className="mt-3 mr-0">
              {paymentInfo.discountPercentForPatientResponsibility
                ? `${paymentInfo.discountPercentForPatientResponsibility}%
              discount`
                : ""}
            </Discount>
          )}
          {paymentInfo.providerFinalAmount && (
            <FieldsContainer>
              <div className="flex align-items-center">
                <label
                  htmlFor="finalPayment"
                  className="mr-2 flex align-items-center"
                >
                  Provider final payment
                </label>

                <InputText
                  disabled
                  id="finalPayment"
                  aria-labelledby="finalPayment"
                  value={paymentInfo.providerFinalAmount}
                />
              </div>
            </FieldsContainer>
          )}
        </div>
      </StyledCard>
    </>
  );
}

export default ServiceDetails;
